import { useState } from 'react'
import { ChevronDown, ChevronUp, FileText, Loader2, Sparkles, Wand2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ImageUpload } from '@/components/ui/image-upload'
import { Input } from '@/components/ui/input'
import { DifficultyIndicator } from '@/components/recipe/difficulty-indicator'
import type { Recipe } from '@/types/recipe'

interface ParsedRecipePreviewProps {
  parsedRecipe: Partial<Recipe>
  onSave: () => Promise<void>
  onRefineRecipe?: (instruction: string) => Promise<void>
  isRefiningRecipe?: boolean
  generateImageOnSave?: boolean
  onGenerateImageOnSaveChange?: (enabled: boolean) => void
  aiImageAvailable?: boolean
  isSaving?: boolean
  onImageChange?: (imageUrl: string) => void
}

export function ParsedRecipePreview({
  parsedRecipe,
  onSave,
  onRefineRecipe,
  isRefiningRecipe = false,
  generateImageOnSave = false,
  onGenerateImageOnSaveChange,
  aiImageAvailable = false,
  isSaving = false,
  onImageChange,
}: ParsedRecipePreviewProps) {
  const navigate = useNavigate()
  const [showAllIngredients, setShowAllIngredients] = useState(false)
  const [showAllInstructions, setShowAllInstructions] = useState(false)
  const [aiInstruction, setAiInstruction] = useState('')

  const ingredients = parsedRecipe.ingredients ?? []
  const instructions = parsedRecipe.instructions ?? []
  const visibleIngredients = showAllIngredients ? ingredients : ingredients.slice(0, 5)
  const visibleInstructions = showAllInstructions ? instructions : instructions.slice(0, 3)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Voorbeeld van je recept</span>
          <Badge variant="secondary">
            <Sparkles className="mr-1 h-3 w-3" />
            Slimme hulp
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold">{parsedRecipe.title}</h3>
          {parsedRecipe.description ? <p className="mt-1 text-gray-600">{parsedRecipe.description}</p> : null}
        </div>

        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
          <label className="flex items-start gap-3 text-sm text-orange-900">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              checked={generateImageOnSave}
              onChange={(event) => onGenerateImageOnSaveChange?.(event.target.checked)}
              disabled={!aiImageAvailable}
            />
            <span>
              Voeg bij het opslaan ook een receptfoto toe
              <span className="mt-1 block text-orange-800">
                Dit is optioneel. Laat het uit als je liever zelf een afbeelding kiest of helemaal geen foto wilt.
              </span>
            </span>
          </label>
        </div>

        <div>
          <h4 className="mb-2 font-medium">Afbeelding</h4>
          {parsedRecipe.image_url ? (
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <img
                src={parsedRecipe.image_url}
                alt={parsedRecipe.title || 'Receptafbeelding'}
                className="aspect-video w-full object-cover"
              />
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
              {generateImageOnSave && aiImageAvailable
                ? 'Bij het opslaan probeert de app een passende receptfoto toe te voegen.'
                : 'Er is nog geen afbeelding ingesteld.'}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
          {parsedRecipe.prep_time ? <span>Voorbereiden: {parsedRecipe.prep_time} min</span> : null}
          {parsedRecipe.cook_time ? <span>Koken: {parsedRecipe.cook_time} min</span> : null}
          {parsedRecipe.servings ? <span>Porties: {parsedRecipe.servings}</span> : null}
          <DifficultyIndicator difficulty={parsedRecipe.difficulty} />
        </div>

        {parsedRecipe.tags && parsedRecipe.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {parsedRecipe.tags.map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        ) : null}

        <div>
          <div className="mb-2 flex items-center justify-between gap-3">
            <h4 className="font-medium">Ingrediënten ({ingredients.length})</h4>
          </div>
          <div className="max-h-56 overflow-y-auto rounded-lg bg-gray-50 p-3">
            <ul className="space-y-1 text-sm">
              {visibleIngredients.map((ingredient, index) => (
                <li key={`${ingredient}-${index}`} className="flex items-start">
                  <span className="mr-2 mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-orange-500"></span>
                  {ingredient}
                </li>
              ))}
              {ingredients.length > 5 ? (
                <li>
                  <button
                    type="button"
                    onClick={() => setShowAllIngredients((current) => !current)}
                    className="inline-flex items-center text-xs text-gray-500 hover:text-orange-600"
                  >
                    {showAllIngredients ? (
                      <>
                        Minder tonen
                        <ChevronUp className="ml-1 h-4 w-4" />
                      </>
                    ) : (
                      <>
                        ... en nog {ingredients.length - 5} meer
                        <ChevronDown className="ml-1 h-4 w-4" />
                      </>
                    )}
                  </button>
                </li>
              ) : null}
            </ul>
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between gap-3">
            <h4 className="font-medium">Bereidingswijze ({instructions.length} stappen)</h4>
          </div>
          <div className="max-h-56 overflow-y-auto rounded-lg bg-gray-50 p-3">
            <ol className="space-y-2 text-sm">
              {visibleInstructions.map((instruction, index) => (
                <li key={`${instruction.slice(0, 20)}-${index}`} className="flex items-start">
                  <span className="mr-2 mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-orange-500 text-xs font-medium text-white">
                    {index + 1}
                  </span>
                  <span className={showAllInstructions ? '' : 'line-clamp-2'}>{instruction}</span>
                </li>
              ))}
              {instructions.length > 3 ? (
                <li className="pl-7">
                  <button
                    type="button"
                    onClick={() => setShowAllInstructions((current) => !current)}
                    className="inline-flex items-center text-xs text-gray-500 hover:text-orange-600"
                  >
                    {showAllInstructions ? (
                      <>
                        Minder tonen
                        <ChevronUp className="ml-1 h-4 w-4" />
                      </>
                    ) : (
                      <>
                        ... en nog {instructions.length - 3} stappen
                        <ChevronDown className="ml-1 h-4 w-4" />
                      </>
                    )}
                  </button>
                </li>
              ) : null}
            </ol>
          </div>
        </div>

        {onImageChange ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="mb-3">
              <h4 className="font-medium text-gray-900">Zelf een foto kiezen</h4>
              <p className="mt-1 text-sm text-gray-600">
                Je kunt ook meteen je eigen foto uploaden of een afbeeldingslink plakken. Dan gebruikt de app die in plaats van een AI-foto.
              </p>
            </div>

            <div className="space-y-3">
              <Input
                value={parsedRecipe.image_url || ''}
                onChange={(event) => onImageChange(event.target.value)}
                placeholder="https://voorbeeld.nl/mijn-receptfoto.jpg"
              />

              <ImageUpload
                onImageUploaded={onImageChange}
                onImageRemoved={() => onImageChange('')}
                existingImages={parsedRecipe.image_url ? [parsedRecipe.image_url] : []}
                maxImages={1}
                disabled={isSaving}
              />
            </div>
          </div>
        ) : null}

        {onRefineRecipe ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="mb-3">
              <h4 className="font-medium text-gray-900">Wil je nog iets veranderen?</h4>
              <p className="mt-1 text-sm text-gray-600">
                Beschrijf gewoon wat je wilt aanpassen. Bijvoorbeeld: &quot;maak dit vegetarisch&quot; of &quot;schrijf de stappen korter&quot;.
              </p>
            </div>

            <textarea
              value={aiInstruction}
              onChange={(event) => setAiInstruction(event.target.value)}
              placeholder="Bijvoorbeeld: maak dit recept geschikt voor 4 personen en voeg broccoli toe."
              className="min-h-[96px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-orange-400"
            />

            <div className="mt-3 flex justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => void onRefineRecipe(aiInstruction)}
                disabled={!aiInstruction.trim() || isRefiningRecipe}
              >
                {isRefiningRecipe ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Bezig met aanpassen...
                  </>
                ) : (
                  <>
                    <Wand2 className="mr-2 h-4 w-4" />
                    Pas voorbeeld aan
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row">
          <Button onClick={() => void onSave()} className="flex-1" disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Bezig met opslaan...
              </>
            ) : (
              <>
                <FileText className="mr-2 h-4 w-4" />
                Opslaan als recept
              </>
            )}
          </Button>
          <Button variant="outline" onClick={() => navigate('/recepten/nieuw', { state: { parsedRecipe } })}>
            Zelf bewerken
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
